import { Alert, Button, DialogActions, DialogContent, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { AutocompleteElement, CheckboxElement, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";

import { AutocompleteModel } from "~/__generated__";
import useAutocompleteGetOptions from "~/modules/app/api/useAutocompleteGetOptions";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import useRequireCurrentClient from "~/modules/client/hooks/useRequireCurrentClient";

import useMapEditorLayerCreate from "../../api/useMapEditorLayerCreate";
import { MapEditorLayerCreateFormData } from "./types";

type Props = {
    projectId: string;
    onFinished: (layerId: string) => void;
    onClose: () => void;
};

export default function MapEditorLayerCreateDialogForm({ projectId, onFinished, onClose }: Props) {
    const { t } = useTranslation("mapEditor");
    const { layerCreate, error, loading } = useMapEditorLayerCreate();
    const [autoCompleteValue, setAutoCompleteValue] = useState("");
    const { loading: loadingProtocols, data } = useAutocompleteGetOptions({
        model: AutocompleteModel.Protocol,
        q: autoCompleteValue,
        params: {
            projectId,
        },
    });

    const onSubmit = async (data: MapEditorLayerCreateFormData) => {
        const result = await layerCreate({ projectId, ...data });
        if (result.data?.mapEditorLayerCreate) {
            onFinished(result.data?.mapEditorLayerCreate.id);
        }
    };

    const { client } = useRequireCurrentClient();

    const {
        control,
        handleSubmit,
        setError,
        formState: { errors, isDirty, isLoading, isValid },
    } = useForm<MapEditorLayerCreateFormData>({
        values: {
            name: "",
            config: {
                showDuringFieldwork: [],
            },
            showToClientUser: client.isAllMapEditorLayersDefaultVisible,
        },
    });
    useApplyErrorToForm(error, setError);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
                <Stack gap={2}>
                    {errors.root && (
                        <Alert severity="error">
                            <Typography>{errors.root.message}</Typography>
                        </Alert>
                    )}

                    <TextFieldElement
                        name="name"
                        size="medium"
                        rules={{ required: true }}
                        control={control}
                        label={t("mapEditorLayerForm.labelName")}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                    />

                    <AutocompleteElement
                        control={control}
                        label={t("mapEditorLayerForm.labelShowDuringFieldwork")}
                        autocompleteProps={{
                            inputValue: autoCompleteValue,
                            onInputChange: (event, newInputValue) => {
                                if (!event) return;
                                setAutoCompleteValue(newInputValue);
                            },
                        }}
                        multiple
                        loading={loadingProtocols}
                        name="config.showDuringFieldwork"
                        options={data?.map(i => ({ id: i.value, label: i.label })) || []}
                    />
                    <CheckboxElement
                        name="showToClientUser"
                        label={t("mapEditorLayerDetails.showToClientUser")}
                        control={control}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("mapEditorLayerForm.btnCancel")}</Button>
                <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!isDirty && isValid}
                    loading={isLoading || loading}
                >
                    {t("mapEditorLayerForm.btnSave")}
                </Button>
            </DialogActions>
        </form>
    );
}
